import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  signal,
  Type
} from "@angular/core";
import { GestureController } from "@ionic/angular/standalone";
import {
  IonContent,
  IonGrid,
  IonRow,
  IonIcon,
  IonCol
} from "@ionic/angular/standalone";
import { CommonModule, NgComponentOutlet } from "@angular/common";
import { ScreenSaverService } from "./screen-saver.service";

export interface ScreenSaverConfig {
  swipeThreshold?: number;
  gestureThreshold?: number;
  background?: string;
  showSwipeIndicator?: boolean;
  autoCloseAfterSeconds?: number;
}

const DEFAULT_CONFIG: Required<ScreenSaverConfig> = {
  swipeThreshold: 100,
  gestureThreshold: 15,
  background: "rgba(0, 0, 0, 0.44)",
  showSwipeIndicator: true,
  autoCloseAfterSeconds: 0
};

@Component({
  selector: "app-screen-saver",
  templateUrl: "./screen-saver.component.html",
  styleUrl: "./screen-saver.component.scss",
  standalone: true,
  imports: [
    IonCol,
    IonGrid,
    IonContent,
    CommonModule,
    IonRow,
    IonIcon,
    NgComponentOutlet
  ]
})
export class ScreenSaverComponent implements AfterViewInit {
  private readonly gestureCtrl = inject(GestureController);
  private readonly el = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly screenSaverService = inject(ScreenSaverService);

  config = input<ScreenSaverConfig>({});
  leftComponent = input<Type<unknown>>();
  rightComponent = input<Type<unknown>>();
  dismissed = output<void>();

  readonly mergedConfig = computed(() => ({
    ...DEFAULT_CONFIG,
    ...this.config()
  }));
  readonly swipeOffset = signal(0);
  readonly isAnimating = signal(false);
  readonly isDismissing = signal(false);
  readonly shineOpacity = computed(() =>
    Math.min(Math.abs(this.swipeOffset()) / 150, 1)
  );
  readonly backgroundStyle = computed(() => this.mergedConfig().background);
  readonly showSwipeIndicator = computed(
    () => this.mergedConfig().showSwipeIndicator
  );

  private gesture: any;
  private autoCloseTimeout: any;

  ngAfterViewInit() {
    this.setupGesture();
    this.setupAutoClose();

    this.destroyRef.onDestroy(() => {
      this.gesture?.destroy();
      this.clearAutoCloseTimeout();
    });
  }

  private setupGesture() {
    const config = this.mergedConfig();
    this.gesture = this.gestureCtrl.create({
      el: this.el.nativeElement,
      gestureName: "swipe-up",
      threshold: config.gestureThreshold,
      direction: "y",
      canStart: (detail) => {
        const selectors = this.screenSaverService.gestureExcludeSelectors;
        if (selectors.size === 0) return true;
        const target = detail.event.target as HTMLElement;
        return !target.closest(Array.from(selectors).join(", "));
      },
      onMove: (ev) => this.onMove(ev),
      onEnd: (ev) => this.onEnd(ev)
    });
    this.gesture.enable();
  }

  private setupAutoClose() {
    const autoCloseSeconds = this.mergedConfig().autoCloseAfterSeconds;
    if (autoCloseSeconds > 0) {
      this.ngZone.runOutsideAngular(() => {
        this.autoCloseTimeout = setTimeout(() => {
          this.ngZone.run(() => this.dismiss());
        }, autoCloseSeconds * 1000);
      });
    }
  }

  private clearAutoCloseTimeout() {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
    }
  }

  private onMove(ev: { deltaY: number }) {
    if (ev.deltaY < 0) {
      this.swipeOffset.set(ev.deltaY);
    }
  }

  private onEnd(ev: { deltaY: number }) {
    if (ev.deltaY < -this.mergedConfig().swipeThreshold) {
      this.dismiss(true);
    } else {
      this.isAnimating.set(true);
      this.swipeOffset.set(0);
      setTimeout(() => this.isAnimating.set(false), 300);
    }
  }

  dismiss(animate = true) {
    this.clearAutoCloseTimeout();
    if (animate) {
      this.isDismissing.set(true);
      this.swipeOffset.set(-window.innerHeight);
      setTimeout(() => {
        this.dismissed.emit();
        this.screenSaverService.dismiss(false);
      }, 250);
    } else {
      this.dismissed.emit();
      this.screenSaverService.dismiss(false);
    }
  }
}
